<template>
    <div>
        <DCard padding>
            <DRow
                align-horizontal="start"
                align-vertical="end"
                class="mb-3"
            >
                <DCol lg="4">
                    <DInput
                        id="search"
                        v-model="filterData.search"
                        :label="__('tools.sepa.sepa_transactions.search')"
                        :placeholder="__('tools.sepa.sepa_transactions.search_helper_text')"
                        @keydown.enter.exact.prevent="filter(transactions.current_page, filterData)"
                    />
                </DCol>
                <DCol
                    lg="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="filter(transactions.current_page, filterData)"
                    />
                    <DButton
                        id="filter-reset"
                        :label="__('messages.actions.reset_filter')"
                        icon="filter-off"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DRow>
                <DCol>
                    <DTable
                        :items="form.transactions"
                        :fields="fields"
                    >
                        <template #booking_date="{item}">
                            <td v-if="!item.booking_user">
                                <div style="display: flex;">
                                    <DDateFormatInput
                                        :id="`booking_date_${item.index}`"
                                        v-model="form.transactions[item.index].booking_date"
                                        format="YYYY-MM-DD"
                                        type="date"
                                        :have-class="'mb-0'"
                                        add-input-classes="bg-light"
                                        class="mb-0"
                                        :invalid-feedback="invalidFeedback(item.index, 'booking_date')"
                                        :is-valid="isValid(item.index, 'booking_date')"
                                        :placeholder="__('tools.sepa.sepa_transactions.table.booking_date')"
                                        size="sm"
                                        style="width: 100%"
                                    />
                                    <div class="pl-2">
                                        <DButton
                                            icon="content-save-edit-outline"
                                            mini
                                            color="success"
                                            :disabled="isInLoadingState"
                                            @click="saveBookingDate(item.index)"
                                        />
                                    </div>
                                </div>
                            </td>
                            <td v-else>
                                {{ setDateFormatEuropeBerlin(form.transactions[item.index].booking_date) }}
                            </td>
                        </template>
                        <template #date="{item}">
                            <td>
                                {{ setDateFormat(item.date) }}
                            </td>
                        </template>
                        <template #total_amount="{item}">
                            <td
                                class="text-right"
                                :class="{'table-text-color-red': item.total_amount < 0}"
                            >
                                {{ priceFormatForDisplayInCurrency(item.total_amount, sepaCurrencyCode) }}
                            </td>
                        </template>
                        <template #upload_type="{item}">
                            <td>{{ __('tools.sepa.sepa_transactions.table.upload_types.'+item.upload_type) }}</td>
                        </template>
                        <template #actions="{item}">
                            <td>
                                <DButton
                                    icon="download"
                                    mini
                                    :disabled="isInLoadingState"
                                    @click="downloadXml(item.id)"
                                />
                            </td>
                        </template>
                    </DTable>
                    <DPagination
                        v-if="transactions.last_page > 1"
                        :active-page="transactions.current_page"
                        :pages="transactions.last_page"
                        align="center"
                        @update:activePage="changePage"
                    />
                </DCol>
            </DRow>
        </DCard>
    </div>
</template>

<script>

import NProgress from 'nprogress'
import _ from 'lodash'
import priceFormat from '~@/Mixins/priceFormat'

export default {
    name: 'SepaTransactionIndex',
    mixins: [
        priceFormat
    ],
    props: {
        transactions: {
            type: Object,
            default: () => { return {} }
        },
        filters: {
            type: Object,
            default () {
                return {}
            }
        }
    },
    data () {
        return {
            // SEPA is a euro-only scheme: a collection is always made in EUR,
            // never in the tenant currency.
            sepaCurrencyCode: 'EUR',
            fields: [
                { key: 'id', label: this.__('tools.sepa.sepa_transactions.table.id') },
                { key: 'date', label: this.__('tools.sepa.sepa_transactions.table.date') },
                { key: 'type', label: this.__('tools.sepa.sepa_transactions.table.type') },
                { key: 'total_amount', label: this.__('tools.sepa.sepa_transactions.table.total_amount') },
                { key: 'booking_date', _style: { minWidth: '25%' }, label: this.__('tools.sepa.sepa_transactions.table.booking_date') },
                { key: 'booking_user', label: this.__('tools.sepa.sepa_transactions.table.booking_user') },
                { key: 'upload_type', label: this.__('tools.sepa.sepa_transactions.table.upload_type') },
                { key: 'actions', label: '' }
            ],
            form: this.$inertia.form({
                transactions: this.transactions.data
            }),
            errors: {},
            filterData: _.cloneDeep(this.filters)
        }
    },
    methods: {
        saveBookingDate (index) {
            this.$inertia.post(
                this.$route('tools.sepa.save-booking-date', { sepaTransaction: this.form.transactions[index].id }),
                { booking_date: this.form.transactions[index].booking_date },
                {
                    onSuccess: () => {
                        this.form.transactions = this.transactions.data
                    },
                    onError: error => {
                        this.errors[`transactions.${index}.booking_date`] = error.booking_date
                        this.form.transactions = this.transactions.data
                    }
                }
            )
        },
        downloadXml (id) {
            NProgress.start()

            window.axios.get(this.$route('tools.sepa.download-xml', { sepaTransaction: id }), {
                responseType: 'blob'
            })
                .then(response => {
                    const blob = new Blob([response.data])

                    const source = URL.createObjectURL(blob)
                    const fileName = response.headers['content-disposition'].match(/filename=(.+)/)[1]

                    const link = document.createElement('a')
                    link.href = source
                    link.download = fileName
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(source)
                })
                .catch(async error => {
                    // With responseType 'blob' the JSON error body arrives as a Blob as well
                    const body = error.response?.data instanceof Blob
                        ? JSON.parse(await error.response.data.text())
                        : error.response?.data

                    this.$page.props.flash.errorMessage = body?.errorMessage ?? this.__('messages.actions.sepa_xml_not_found')
                })
                .finally(() => {
                    NProgress.done()
                })
        },
        isValid (index, field) {
            if (typeof this.errors[`transactions.${index}.${field}`] !== 'undefined') {
                return false
            }

            return null
        },
        invalidFeedback (index, field) {
            if (this.isValid(index, field) === false) {
                return this.errors[`transactions.${index}.${field}`]
            }

            return null
        },
        changePage (page) {
            this.filter(page, this.filterData)
        },
        filter (page, filters, defaultFilters = false) {
            const queryParameters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
            const routeParameters = {
                saveFilters: true
            }

            if (page > 1) {
                queryParameters.page = page
            }

            if (defaultFilters === true) {
                routeParameters.defaultFilters = true
            }

            this.$inertia.get(this.$route('tools.sepa.transactions', routeParameters), queryParameters)
        },
        resetFilters () {
            this.filter(1, {}, true)
        }
    }
}
</script>

<style scoped>

</style>
